/**
 * Task #2142 — Utilities condivise BE/FE per il dashboard AI Routing.
 *
 * Single source of truth per:
 *   - `PROVIDER_META`: catalogo provider canonical + factory per custom endpoint
 *     (label, dotColor, kind). Sostituisce la duplicazione storica
 *     `PROVIDER_META` inline in `ServiceRoutingTab` + `PROVIDER_METADATA`
 *     in `packages/frontend/src/constants/ai-providers.ts`.
 *   - `isServicePlatformScoped(serviceKey)`: predicato puro sopra
 *     `SERVICE_CAPABILITY_MAP`. Vietato duplicarlo inline nelle UI.
 *   - `EMBEDDER_DOWNSTREAM_KEYS`: lista dei service_key downstream il cui
 *     stato (`provider`, `model`, `providers_available`) DEVE essere
 *     ricalcolato quando cambia l'embedder cluster-wide (policy o backfill).
 *     Usata dal backend per emettere `invalidates_keys` in
 *     `side_effects` su PUT `master/embeddings/policy` e dal FE per scartare
 *     entries ottimistiche e attendere il prossimo `routing-matrix`.
 *   - `RoutingMatrixEntry` / `RoutingMatrixResponse`: contratto canonico
 *     `GET /api/ai-tools/routing-matrix`.
 *   - Eventi WS tipizzati `RoutingMatrixSocketEvent`.
 */
import { z } from 'zod';
import { SERVICE_CAPABILITY_MAP, getServiceDescriptor } from './ai-routing/service-capability-map';
import {
  AvailableProviderEntrySchema,
  type AvailableProviderEntry,
  ProviderRefSchema,
  type ProviderRef,
  isCustomProviderRef,
  parseCustomProviderRef,
} from './schemas/ai-routing';

// ---------------------------------------------------------------------------
// PROVIDER_META — catalogo canonical + factory custom
// ---------------------------------------------------------------------------

export type ProviderKind = 'canonical' | 'custom';

export interface ProviderMeta {
  id: ProviderRef;
  label: string;
  /** Colore badge "live" UI (CSS color string). */
  dotColor: string;
  kind: ProviderKind;
}

export const CANONICAL_PROVIDER_META: Readonly<Record<'openai' | 'claude' | 'google', ProviderMeta>> = {
  openai: { id: 'openai', label: 'OpenAI', dotColor: '#10a37f', kind: 'canonical' },
  claude: { id: 'claude', label: 'Claude', dotColor: '#cc785c', kind: 'canonical' },
  google: { id: 'google', label: 'Google Gemini', dotColor: '#4285f4', kind: 'canonical' },
};

export const NONE_PROVIDER_META: ProviderMeta = {
  id: 'none',
  label: 'Nessun provider',
  dotColor: '#9ca3af',
  kind: 'canonical',
};

/**
 * Builda il `ProviderMeta` di un endpoint custom (`custom:<uuid>`) dato il nome
 * visibile dall'endpoint (`custom_llm_endpoints.name`). Il dotColor è quello
 * del custom (viola scuro), il label include il nome operatore.
 */
export function buildCustomProviderMeta(endpointId: string, endpointName: string): ProviderMeta {
  return {
    id: `custom:${endpointId}`,
    label: `${endpointName} (custom)`,
    dotColor: '#6d28d9',
    kind: 'custom',
  };
}

/**
 * Risolve il `ProviderMeta` per un `ProviderRef` arbitrario. Per i custom
 * richiede una lookup table `endpointNameById` (passata dal chiamante che
 * ha già la lista degli endpoint del tenant). Se il custom UUID non è in
 * lookup, ritorna un meta degraded `(custom)` con UUID troncato.
 */
export function resolveProviderMeta(
  ref: ProviderRef,
  endpointNameById?: ReadonlyMap<string, string>,
): ProviderMeta {
  if (ref === 'none') return NONE_PROVIDER_META;
  if (ref in CANONICAL_PROVIDER_META) {
    return CANONICAL_PROVIDER_META[ref as 'openai' | 'claude' | 'google'];
  }
  if (isCustomProviderRef(ref)) {
    const id = parseCustomProviderRef(ref);
    if (id) {
      const name = endpointNameById?.get(id);
      return buildCustomProviderMeta(id, name ?? `endpoint ${id.slice(0, 8)}…`);
    }
  }
  return { id: ref, label: ref, dotColor: '#9ca3af', kind: 'canonical' };
}

// ---------------------------------------------------------------------------
// isServicePlatformScoped — predicato puro sopra SERVICE_CAPABILITY_MAP
// ---------------------------------------------------------------------------

/**
 * True se il tenant NON può scegliere provider/modello del `service_key`
 * (oggi solo `embedding`, deciso dal lock dell'embedder). Tutti gli altri
 * service_key sono instradabili per tenant via `/ai-tools/service-provider`
 * e `/ai-tools/service-models/:key`; il default di piattaforma resta gestito
 * dal master via `/master/platform-models`.
 *
 * Service key sconosciuto → false (fail-soft).
 */
export function isServicePlatformScoped(serviceKey: string): boolean {
  const d = getServiceDescriptor(serviceKey);
  return d ? !d.tenant_routable : false;
}

// ---------------------------------------------------------------------------
// EMBEDDER_DOWNSTREAM_KEYS — service_key impattati da un cambio embedder
// ---------------------------------------------------------------------------

/**
 * Quando cambia l'embedder cluster-wide (PUT `/master/embeddings/policy`),
 * TUTTE le pipeline RAG
 * downstream devono rileggere la matrix: il provider e il modello dell'embedder
 * cambiano, ma soprattutto il `providers_available` di queste rotte può
 * shifftare (es. un modello chat che richiedeva un embedder specifico smette
 * di matchare). Lista enumerata esplicitamente per non includere service_key
 * non-RAG (TTS/STT) che non hanno dipendenza dall'embedder.
 */
export const EMBEDDER_DOWNSTREAM_KEYS: ReadonlyArray<string> = [
  'embedding',
  'rag_synthesis',
  'rag_synthesis_multimodal',
  'rag_synthesis_multimodal_fallback',
  'rag_decomposition',
  'rag_rerank',
  'rag_rerank_secondary',
  'rag_eval',
  'rag_query_expansion_model',
  'rag_query_expansion',
  'rag_chunk_context',
  'rag_topk_strategy',
  'rag_quality_features',
];

// ---------------------------------------------------------------------------
// Routing matrix — contratto GET /api/ai-tools/routing-matrix
// ---------------------------------------------------------------------------

export const RoutingMatrixEntrySchema = z.object({
  service_key: z.string().min(1),
  /** Label UI breve (proviene da `SERVICE_CAPABILITY_MAP.label_it`). */
  label_it: z.string(),
  /** Capability richiesta dal service_key. */
  capability: z.string(),
  /** True = governato a livello piattaforma (super_admin), false = tenant. */
  platform_scoped: z.boolean(),
  /** Provider attivo per il tenant (tenant_service_routing OR platform_model_config). */
  provider: ProviderRefSchema,
  /** Modello attivo. Stringa vuota se non configurato. */
  model: z.string(),
  /** Source del valore corrente (per indicare al FE da dove arriva). */
  source: z.enum(['tenant', 'platform', 'default']),
  /** True se il valore del tenant viene dalla configurazione consigliata per embedder (preset). */
  preset: z.boolean(),
  /**
   * Stato del modello attivo nel catalogo: `deprecating`/`unavailable` vanno
   * segnalati in dashboard. null = nessun modello o modello assente dal catalogo.
   */
  model_status: z.enum(['active', 'deprecating', 'unavailable']).nullable(),
  /** Lista dei provider candidati per questo service_key (capability-filtered). */
  providers_available: z.array(AvailableProviderEntrySchema),
});

export type RoutingMatrixEntry = z.infer<typeof RoutingMatrixEntrySchema>;

export const RoutingMatrixSettingsSchema = z.object({
  /** RAG cache threshold per-tenant (null = usa global). */
  rag_cache_threshold: z.number().nullable(),
  /** RAG rerank boost multimodal (0..1). */
  rag_rerank_multimodal_boost: z.number().nullable(),
  /** OCR timeout in millisecondi (0 = default infra). */
  ocr_timeout_ms: z.number().int().nonnegative().nullable(),
  /** RAG plus enabled cluster-wide (platform flag). */
  rag_plus_enabled: z.boolean(),
});
export type RoutingMatrixSettings = z.infer<typeof RoutingMatrixSettingsSchema>;

export const RoutingMatrixResponseSchema = z.object({
  /** Monotonic version, incrementa ad ogni invalidate. Il FE usa per reconcile. */
  version: z.number().int().nonnegative(),
  /** Unix ms del momento in cui la matrix è stata computata (cache miss). */
  generated_at: z.number().int().nonnegative(),
  /** TTL secondi della cache L1 al momento della build. */
  cache_ttl_seconds: z.number().int().nonnegative(),
  /** True se la response viene da L1 cache (debug/observability). */
  from_cache: z.boolean(),
  entries: z.array(RoutingMatrixEntrySchema),
  settings: RoutingMatrixSettingsSchema,
});

export type RoutingMatrixResponse = z.infer<typeof RoutingMatrixResponseSchema>;

// ---------------------------------------------------------------------------
// PUT response diff — side_effects.invalidates_keys + version
// ---------------------------------------------------------------------------

export const RoutingMutationSideEffectsSchema = z.object({
  /**
   * Lista dei `service_key` la cui entry (provider/model/providers_available)
   * potrebbe essere cambiata come effetto del mutation. Il FE usa per:
   *   1. droppare le entries ottimistiche di queste key,
   *   2. mostrare uno spinner inline finché il nuovo `routing-matrix` arriva.
   * Per i mutation tenant-scoped contiene solo il `service_key` toccato; per
   * cambio embedder contiene tutti gli `EMBEDDER_DOWNSTREAM_KEYS`.
   */
  invalidates_keys: z.array(z.string()),
  /** Nuovo `version` della matrix lato BE post-write. Il FE usa per reconcile. */
  matrix_version: z.number().int().nonnegative(),
});
export type RoutingMutationSideEffects = z.infer<typeof RoutingMutationSideEffectsSchema>;

export const RoutingMutationResponseSchema = z.object({
  success: z.literal(true),
  data: z.unknown(),
  side_effects: RoutingMutationSideEffectsSchema,
});
export type RoutingMutationResponse = z.infer<typeof RoutingMutationResponseSchema>;

// ---------------------------------------------------------------------------
// WebSocket events (tenant room `tenant:${tenantId}`)
// ---------------------------------------------------------------------------

export const ROUTING_SOCKET_EVENTS = {
  /** Stato `is_valid` o `health_status` di un provider è cambiato. */
  PROVIDER_STATUS_CHANGED: 'ai:routing:provider_status_changed',
  /** Matrix invalidata (qualsiasi write site). Il FE fa refetch debounced. */
  MATRIX_INVALIDATED: 'ai:routing:matrix_invalidated',
} as const;

export type RoutingSocketEventName = (typeof ROUTING_SOCKET_EVENTS)[keyof typeof ROUTING_SOCKET_EVENTS];

export const ProviderStatusChangedPayloadSchema = z.object({
  provider: ProviderRefSchema,
  is_valid: z.boolean(),
  /** Solo per custom: `ok|degraded|down|unknown`. */
  health_status: z.string().optional(),
  /** Version della matrix BE al momento dell'event. */
  matrix_version: z.number().int().nonnegative(),
});
export type ProviderStatusChangedPayload = z.infer<typeof ProviderStatusChangedPayloadSchema>;

export const MatrixInvalidatedPayloadSchema = z.object({
  /** Lista dei service_key potenzialmente invalidi (sottoinsieme di RoutingMatrixEntry.service_key). */
  invalidates_keys: z.array(z.string()),
  /** Nuova versione attesa dopo refetch. */
  matrix_version: z.number().int().nonnegative(),
  /** Hint sulla causa per il logging client. */
  origin: z.string().optional(),
});
export type MatrixInvalidatedPayload = z.infer<typeof MatrixInvalidatedPayloadSchema>;

// Re-export per centralizzare l'import client.
export type { AvailableProviderEntry, ProviderRef };
export { isCustomProviderRef, parseCustomProviderRef, SERVICE_CAPABILITY_MAP, getServiceDescriptor };
